import React, { useState, useEffect, useCallback } from 'react';
import { Form, Item } from 'devextreme-react/form';
import { Button } from 'devextreme-react/button';
import { DateBox } from 'devextreme-react/date-box';
import { TagBox } from 'devextreme-react/tag-box';
import { CheckBox } from 'devextreme-react/check-box';
import { createStore } from 'devextreme-aspnet-data-nojquery';
import { useMsal } from "@azure/msal-react";
import { getApiToken } from '../../utils/authUtils.js';
import notify from 'devextreme/ui/notify';
import { Item as ToolbarItem, Toolbar } from 'devextreme-react/toolbar';
import ScrollView from 'devextreme-react/scroll-view';
import { useNavigate } from 'react-router-dom';
import './report2.scss';
import { openPDFOnDevice } from '../../utils/pdfUtils.js';
import { COURSES } from '../../shared/constants.js';

export default function Page() {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const [coursesData, setCoursesData] = useState(COURSES);
  const [trainingGroupsData, setTrainingGroupsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Form data state
  const [formData, setFormData] = useState({
    courses: [],
    traininggroup_ids: [],
    startdate: new Date(2000, 0, 1), // January 1st of current year
    enddate: new Date(), // Today
    rudolph: true,
    wa: false
  });

  const onLeftClick = () => {
    navigate(-1);
  };

  // Load initial data
  useEffect(() => {
    getApiToken(instance, accounts).then(token => {


      // Load training groups
      const trainingGroupsStore = createStore({
        key: 'traininggroupId',
        loadUrl: `${process.env.REACT_APP_serverEndpoint}/Traininggroup/TraininggroupsBasic`,
        onBeforeSend: (method, ajaxOptions) => {
          ajaxOptions.headers = {
            'Authorization': "Bearer " + token
          };
        },
      });

      trainingGroupsStore.load().then(data => {
        setTrainingGroupsData(data);
      }).catch(error => {
        console.error('Error loading training groups:', error);
      });
    });
  }, [instance, accounts]);

  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => {
      // Wenn Rudolph aktiviert wird, deaktiviere WA
      if (field === 'rudolph' && value === true) {
        return {
          ...prev,
          [field]: value,
          wa: false
        };
      }
      // Wenn WA aktiviert wird, deaktiviere Rudolph
      if (field === 'wa' && value === true) {
        return {
          ...prev,
          [field]: value,
          rudolph: false
        };
      }
      // Für alle anderen Felder normale Behandlung
      return {
        ...prev,
        [field]: value
      };
    });
  }, []);

  const generateReport = useCallback(() => {
    if (!formData.courses.length || !formData.traininggroup_ids.length) {
      notify({
        message: 'Bitte wählen Sie mindestens einen Kurs und eine Trainingsgruppe aus.',
        position: { at: 'bottom center', my: 'bottom center' }
      }, 'warning');
      return;
    }

    if (!formData.rudolph && !formData.wa) {
      notify({
        message: 'Bitte wählen Sie entweder Rudolph oder WA Punkte aus.',
        position: { at: 'bottom center', my: 'bottom center' }
      }, 'warning');
      return;
    }

    setIsLoading(true);
    getApiToken(instance, accounts).then(token => {
      // Create the request object matching Top3RankingRequest
      const requestBody = {
        courses: formData.courses,
        traininggroup_ids: formData.traininggroup_ids,
        startdate: formData.startdate.toISOString(),
        enddate: formData.enddate.toISOString(),
        rudolph: formData.rudolph,
        wa: formData.wa
      };

      // Call the GetTop3RankingReport API
      fetch(`${process.env.REACT_APP_serverEndpoint}/Report/GetTop3RankingReport`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.blob();
      })
      .then(async blob => {
        var filename = `Top3_Ranking_${new Date().toISOString().split('T')[0]}.pdf`;
        await openPDFOnDevice(blob, filename);

        notify({
          message: 'PDF-Bericht wurde erfolgreich heruntergeladen.',
          position: { at: 'bottom center', my: 'bottom center' }
        }, 'success');
      })
      .catch(error => {
        console.error('Error generating report:', error);
        notify({
          message: 'Fehler beim Generieren des Berichts.',
          position: { at: 'bottom center', my: 'bottom center' }
        }, 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
    });
  }, [formData, instance, accounts]);

  return (
    <ScrollView className='view-wrapper-scroll'>
      <div className='view-wrapper view-wrapper-report-details'>
        <Toolbar className='toolbar-details theme-dependent'>
          <ToolbarItem location='before'>
            <Button icon='arrowleft' stylingMode='text' onClick={onLeftClick} />
          </ToolbarItem>
          <ToolbarItem location='before' text='Top 3 Ranking Bericht' />
          <ToolbarItem location='after'>
            <Button
              text="PDF-Bericht generieren"
              type="success"
              icon="pdffile"
              onClick={generateReport}
              disabled={isLoading}
            />
          </ToolbarItem>
        </Toolbar>

        <div className='panels'>
          <Form formData={formData} labelLocation="top" className='report-form' colCount={3}>
              <Item dataField="courses" label={{ text: 'Bahnlänge' }}>
                <TagBox
                  dataSource={coursesData}
                  valueExpr="id"
                  displayExpr="text"
                  placeholder="Bahnlänge auswählen..."
                  showSelectionControls={true}
                  value={formData.courses}
                  onValueChanged={(e) => handleFieldChange('courses', e.value)}
                />
              </Item>
              
              <Item dataField="traininggroup_ids" label={{ text: 'Trainingsgruppen' }}>
                <TagBox
                  dataSource={trainingGroupsData}
                  valueExpr="traininggroupId"
                  displayExpr="groupname"
                  placeholder="Trainingsgruppen auswählen..."
                  showSelectionControls={true}
                  value={formData.traininggroup_ids}
                  onValueChanged={(e) => handleFieldChange('traininggroup_ids', e.value)}
                />
              </Item>

              <Item dataField="startdate" label={{ text: 'Startdatum' }}>
                <DateBox
                  placeholder="Startdatum"
                  value={formData.startdate}
                  onValueChanged={(e) => handleFieldChange('startdate', e.value)}
                />
              </Item>

              <Item dataField="enddate" label={{ text: 'Enddatum' }}>
                <DateBox
                  placeholder="Enddatum"
                  value={formData.enddate}
                  onValueChanged={(e) => handleFieldChange('enddate', e.value)}
                />
              </Item>

              <Item dataField="rudolph" label={{ visible: false }}>
                <CheckBox
                  text="nach Rudolph-Punkten"
                  value={formData.rudolph}
                  onValueChanged={(e) => handleFieldChange('rudolph', e.value)}
                />
              </Item>

              <Item dataField="wa" label={{ visible: false }}>
                <CheckBox
                  text="Sortierung nach WA-Punkten"
                  value={formData.wa}
                  onValueChanged={(e) => handleFieldChange('wa', e.value)}
                />
              </Item>
            </Form>
            {isLoading && (
              <div className='loading-info'>
                <p>Bericht wird generiert...</p>
              </div>
            )}
        </div>
       
      </div>
    </ScrollView>
  );
}
